import clsx from 'clsx';

// Hooks
import useAcl from '@/hooks/user/useAcl';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { aclFeatures } from '@/lib/acl/aclFeatures';
// Lib
import { STATUS } from '@/lib/socialprofiles/status';
import { ReAuthCTA } from '@/hooks/socialprofiles/useGetSocialProfileStyle/ReAuthCTA';

// Components
import Icon from '@/components/base/Icon';
import { DisplayedSocialProfileType } from '@/components/pages/socialprofiles/SocialProfilesList';

type useGetSocialProfileStyleProps = {
    socialProfile: DisplayedSocialProfileType;
    onDelete: () => void;
};

type ComponentType = { className?: string };

const useGetSocialProfileStyle = ({ socialProfile, onDelete }: useGetSocialProfileStyleProps) => {
    const { t } = useTranslation('socialprofiles');
    const { hasAcl } = useAcl();

    switch (socialProfile.displayedStatus) {
        case STATUS.active:
            return {
                borderColor: 'border-icoGray-300 dark:border-icoDarkMode-beton',

                Label: ({ className }: ComponentType) => (
                    <p className={clsx('text-icoGreen', className)}>
                        <Icon className="mr-2" icon="check" />
                        {t('connected')}
                    </p>
                ),
                StatusText: () => null
            };
        case STATUS.inactive:
            return {
                borderColor: 'border-icoGray-300 dark:border-icoDarkMode-beton',
                Label: ({ className }: ComponentType) => (
                    <div className={clsx('text-icoGray-500', className)}>
                        <p
                            className={clsx({
                                'mr-4 border-r border-icoGray-300 pr-4 dark:border-icoDarkMode-beton':
                                    hasAcl(aclFeatures.social_profile.base.delete)
                            })}>
                            {t('deactivated')}
                        </p>
                        {hasAcl(aclFeatures.social_profile.base.delete) ? (
                            <button
                                data-testid={`delete-social-profile-${socialProfile.id}`}
                                onClick={onDelete}
                                className="rounded-none bg-none p-0">
                                <Icon icon="trash" />
                            </button>
                        ) : null}
                    </div>
                ),
                StatusText: () => null
            };
        case STATUS.reAuth:
        case STATUS.reAuthSchedulerPerms:
        case STATUS.userTeamSchedulerPerms: {
            const labelClasses = 'bg-icoRed-100 dark:bg-icoRed/20';
            const labelText = t('reauth');
            return {
                borderColor: 'border-icoRed',
                Label: ({ className }: ComponentType) => (
                    <p className={clsx('text-icoRed', className)}>
                        <Icon className="mr-2" icon="warning" />
                        <ReAuthCTA
                            className={labelClasses}
                            label={labelText}
                            socialProfile={socialProfile}
                        />
                    </p>
                ),
                StatusText: ({ className }: ComponentType) => (
                    <p className={clsx('bg-icoRed-100 text-icoRed dark:bg-icoRed/20', className)}>
                        {t(socialProfile.auth_status.error) +
                            ' #' +
                            socialProfile.auth_status.debug}
                    </p>
                )
            };
        }
        case STATUS.disconnected:
            return {
                borderColor: 'border-icoRed',
                Label: ({ className }: ComponentType) => (
                    <p className={clsx('text-icoRed', className)}>
                        <Icon className="mr-2" icon="warning" />
                        {t('disconnected')}
                    </p>
                ),
                StatusText: ({ className }: ComponentType) => (
                    <p className={clsx('bg-icoRed-100 text-icoRed dark:bg-icoRed/20', className)}>
                        {t(socialProfile.auth_status.error) +
                            ' #' +
                            socialProfile.auth_status.debug}
                    </p>
                )
            };
        case STATUS.refresh: {
            const labelClasses = 'bg-icoYellow-100 dark:bg-icoYellow/20 ';
            const labelText = t('refresh_access');
            return {
                borderColor: 'border-icoYellow-200',
                Label: ({ className }: ComponentType) => (
                    <p className={clsx('text-icoYellow', className)}>
                        <Icon className="mr-2" icon="warning" />
                        <ReAuthCTA
                            className={labelClasses}
                            label={labelText}
                            socialProfile={socialProfile}
                        />
                    </p>
                ),
                StatusText: ({ className }: ComponentType) => (
                    <p
                        className={clsx(
                            'bg-icoYellow-100 text-icoYellow dark:bg-icoYellow/20',
                            className
                        )}>
                        {t(socialProfile.auth_status.error) +
                            ' #' +
                            socialProfile.auth_status.debug}
                    </p>
                )
            };
        }
        case STATUS.expiringSoon:
            return {
                borderColor: 'border-icoYellow-200',
                Label: ({ className }: ComponentType) => (
                    <p className={clsx('text-icoYellow', className)}>
                        <Icon className="mr-2" icon="warning" />
                        {t('expiring_soon')}
                    </p>
                ),
                StatusText: ({ className }: ComponentType) => (
                    <p
                        className={clsx(
                            'bg-icoYellow-100 text-icoYellow dark:bg-icoYellow/20',
                            className
                        )}>
                        {t(socialProfile.auth_status.error) +
                            ' #' +
                            socialProfile.auth_status.debug}
                    </p>
                )
            };
        default:
            return { borderColor: '', Label: () => null, StatusText: () => null };
    }
};

export default useGetSocialProfileStyle;
